// Interactive activities for Domain 04 (WAN technologies). Same contract as
// ./widgets.js: { id, blocker, required?, mount(el, rt, api), verify(data, rt) } — verify() is the
// completion rule without the DOM, used by the server replay (src/core/labVerify.js).
// Everything is computed, never scripted: topology resilience comes from a graph
// search, congestion results from the same allocator the simulator uses.

import { h, svg } from './h.js';
import { allocate } from '../engine/net/qos.js';

// ------------------------------------------------------------------ labelled diagram
/**
 * Name the numbered parts of a diagram. `parts` = [{ n, x, y, answer, why }],
 * `draw(g)` adds the artwork to an SVG group (viewBox 0 0 W H).
 */
export function hotspots({ id, title, intro, width = 900, height = 220, draw, parts, options }) {
  return {
    id, blocker: `Complete: ${title}`,
    verify: (d) => parts.every((p) => d?.pick?.[p.n] === p.answer),
    mount(el, rt, api) {
      const s = api.state.data;
      s.pick = s.pick || {};
      const render = (checked) => {
        el.textContent = '';
        el.append(h('div', { class: 'w-title' }, title, api.state.done ? h('span', { class: 'ok' }, '✓ COMPLETE') : null));
        if (intro) el.append(h('p', { class: 'small muted' }, intro));
        const root = svg('svg', { viewBox: `0 0 ${width} ${height}`, class: 'wdiagram', role: 'img', 'aria-label': title });
        draw(root);
        for (const p of parts) {
          const ok = checked && s.pick[p.n] === p.answer;
          root.append(svg('g', { class: `hs ${checked ? (ok ? 'good' : 'bad') : ''}` }, svg('circle', { cx: p.x, cy: p.y, r: 13 }), svg('text', { x: p.x, y: p.y + 4.5, 'text-anchor': 'middle' }, String(p.n))));
        }
        el.append(h('div', { class: 'wdiagram-wrap' }, root));
        const g = h('div', { class: 'w-grid' });
        for (const p of parts) {
          const val = s.pick[p.n] || '';
          const bad = checked && val !== p.answer;
          g.append(h('div', { class: `w-row ${checked && !bad ? 'good' : ''} ${bad ? 'bad' : ''}` },
            h('b', {}, `${p.n}.`),
            h('select', { 'data-testid': `hs-${id}-${p.n}`, disabled: api.state.done || null, onchange: (e) => { s.pick[p.n] = e.target.value; api.save(); } },
              h('option', { value: '' }, 'Choose…'), options.map((o) => h('option', { value: o, selected: val === o || null }, o))),
            bad && p.why ? h('div', { class: 'w-fb bad' }, p.why) : null));
        }
        el.append(g);
        if (!api.state.done) el.append(h('div', { class: 'row' }, h('button', { class: 'btn small', 'data-testid': `hs-check-${id}`, onclick: () => {
          const all = parts.every((p) => s.pick[p.n] === p.answer);
          rt.action('hotspots', { widget: id, ok: all });
          if (all) api.done();
          render(true);
        } }, 'Check labels')));
      };
      render(api.state.done);
    },
  };
}

// ------------------------------------------------------------------ WAN topology explorer
const SITES = ['HQ', 'Batangas', 'Cebu', 'Davao', 'Iloilo'];
export const TOPOLOGIES = {
  p2p: { label: 'Point-to-point', sites: 2, links: [[0, 1]] },
  hub: { label: 'Hub-and-spoke', sites: 5, links: [[0, 1], [0, 2], [0, 3], [0, 4]] },
  full: { label: 'Full mesh', sites: 5, links: [[0, 1], [0, 2], [0, 3], [0, 4], [1, 2], [1, 3], [1, 4], [2, 3], [2, 4], [3, 4]] },
  partial: { label: 'Partial mesh', sites: 5, links: [[0, 1], [0, 2], [0, 3], [0, 4], [1, 2], [3, 4]] },
};
/** Sites that can still reach HQ (index 0) when some links/sites have failed. */
export function reachableFromHq(topo, failedLinks = [], failedSites = []) {
  const t = TOPOLOGIES[topo];
  if (failedSites.includes(0)) return [];
  const seen = new Set([0]); const q = [0];
  while (q.length) {
    const a = q.shift();
    t.links.forEach(([x, y], k) => {
      if (failedLinks.includes(k)) return;
      const b = x === a ? y : y === a ? x : null;
      if (b === null || seen.has(b) || failedSites.includes(b)) return;
      seen.add(b); q.push(b);
    });
  }
  return [...seen];
}
export function topoExplorer({ id }) {
  return {
    id, blocker: 'Explore every topology and fail a link and a site',
    // an exploration: done once every topology was viewed and both failure experiments were tried
    verify: (d) => Object.keys(TOPOLOGIES).every((k) => d?.seen?.[k] === true) && d?.tried?.hubSite === true && d?.tried?.meshLink === true,
    mount(el, rt, api) {
      const s = api.state.data;
      s.seen = s.seen || {}; s.tried = s.tried || {};
      s.topo = s.topo || 'p2p'; s.failL = s.failL || []; s.failS = s.failS || [];
      const goal = () => Object.keys(TOPOLOGIES).every((k) => s.seen[k]) && s.tried.hubSite && s.tried.meshLink;
      const render = () => {
        el.textContent = '';
        const t = TOPOLOGIES[s.topo];
        el.append(h('div', { class: 'w-title' }, 'WAN TOPOLOGY EXPLORER', api.state.done ? h('span', { class: 'ok' }, '✓ COMPLETE') : null),
          h('p', { class: 'small muted' }, 'Pick a topology, then click a circuit or a site to fail it. The explorer shows which sites still reach HQ.'));
        el.append(h('div', { class: 'opt-row' }, Object.entries(TOPOLOGIES).map(([k, v]) => h('button', { class: `opt ${s.topo === k ? 'on' : ''}`, 'data-testid': `topo-${k}`, onclick: () => { s.topo = k; s.failL = []; s.failS = []; s.seen[k] = true; rt.action('topo-view', { topo: k }); api.save(); render(); } }, v.label))));
        s.seen[s.topo] = true;
        const W = 460, H = 260, cx = 230, cy = 135;
        const pos = (i) => (t.sites === 2 ? [[110, 135], [350, 135]][i] : i === 0 && s.topo === 'hub' ? [cx, cy] : [cx + 165 * Math.cos(-Math.PI / 2 + (2 * Math.PI * (s.topo === 'hub' ? i - 1 : i)) / (s.topo === 'hub' ? 4 : 5)), cy + 105 * Math.sin(-Math.PI / 2 + (2 * Math.PI * (s.topo === 'hub' ? i - 1 : i)) / (s.topo === 'hub' ? 4 : 5))]);
        const reach = reachableFromHq(s.topo, s.failL, s.failS);
        const root = svg('svg', { viewBox: `0 0 ${W} ${H}`, class: 'wdiagram', role: 'img', 'aria-label': `${t.label} topology` });
        t.links.forEach(([a, b], k) => {
          const [x1, y1] = pos(a); const [x2, y2] = pos(b);
          const failed = s.failL.includes(k);
          root.append(svg('line', { x1, y1, x2, y2, class: `wl ${failed ? 'failed' : ''}`, 'data-testid': `topo-link-${k}`, onclick: () => { s.failL = failed ? s.failL.filter((x) => x !== k) : [...s.failL, k]; if (!failed && (s.topo === 'full' || s.topo === 'partial')) s.tried.meshLink = true; rt.action('topo-fail', { topo: s.topo, link: k }); api.save(); render(); } }));
        });
        for (let i = 0; i < t.sites; i++) {
          const [x, y] = pos(i);
          const failed = s.failS.includes(i);
          const ok = reach.includes(i);
          root.append(svg('g', { class: `ws ${failed ? 'failed' : ok ? 'ok' : 'cut'}`, 'data-testid': `topo-site-${i}`, onclick: () => { s.failS = failed ? s.failS.filter((x) => x !== i) : [...s.failS, i]; if (!failed && s.topo === 'hub' && i === 0) s.tried.hubSite = true; rt.action('topo-fail-site', { topo: s.topo, site: i }); api.save(); render(); } },
            svg('circle', { cx: x, cy: y, r: 24 }), svg('text', { x, y: y + 4, 'text-anchor': 'middle' }, SITES[i])));
        }
        el.append(h('div', { class: 'wdiagram-wrap' }, root));
        const isolated = t.sites - reach.length - s.failS.filter((x) => x !== 0).length;
        const spof = TOPOLOGIES[s.topo].links.filter((_, k) => reachableFromHq(s.topo, [k]).length < t.sites).length;
        el.append(h('div', { class: 'w-grid' },
          h('div', { class: 'w-row' }, h('b', {}, 'Circuits to buy'), h('span', { 'data-testid': 'topo-links' }, String(t.links.length))),
          h('div', { class: 'w-row' }, h('b', {}, 'Circuits whose failure isolates a site'), h('span', { 'data-testid': 'topo-spof' }, String(spof))),
          h('div', { class: 'w-row' }, h('b', {}, 'Sites cut off from HQ right now'), h('span', { 'data-testid': 'topo-isolated' }, String(Math.max(0, s.failS.includes(0) ? t.sites - 1 : isolated))))));
        const left = [...Object.keys(TOPOLOGIES).filter((k) => !s.seen[k]).map((k) => `view ${TOPOLOGIES[k].label}`), ...(!s.tried.hubSite ? ['fail the HQ hub in hub-and-spoke'] : []), ...(!s.tried.meshLink ? ['fail a circuit in a mesh'] : [])];
        if (!api.state.done && goal()) api.done();
        el.append(h('p', { class: 'small muted' }, api.state.done ? 'Explored. Compare cost, single points of failure and blast radius before you answer below.' : `Still to try: ${left.join(' · ')}`));
      };
      render();
    },
  };
}

// ------------------------------------------------------------------ congestion simulator
/** T1 with voice + bulk traffic; the learner changes the load and toggles LLQ. */
export function congestionSim({ id, cap = 1544, voice = 174 }) {
  return {
    id, blocker: 'Run the congestion simulation with QoS off and on',
    verify: (d) => d?.seen?.badNoQos === true && d?.seen?.goodQos === true,
    mount(el, rt, api) {
      const s = api.state.data;
      s.bulk ??= 600; s.qos ??= false; s.seen = s.seen || {};
      const render = () => {
        el.textContent = '';
        const classes = [{ name: 'voice', offered: voice, priority: s.qos ? 256 : 0 }, { name: 'bulk', offered: +s.bulk, priority: 0 }];
        const a = s.qos ? allocate(cap, classes) : allocate(cap, classes.map((c) => ({ ...c, priority: 0 })));
        // FIFO: everyone loses the same share
        const fifo = !s.qos && a.congested ? { voice: (voice * cap) / (voice + +s.bulk), bulk: (+s.bulk * cap) / (voice + +s.bulk) } : null;
        const served = fifo || a.served;
        const vLoss = 100 * (1 - served.voice / voice);
        const jitter = !a.congested ? 2 : s.qos ? 3 : 45;
        const mos = Math.max(1, Math.min(4.4, 4.4 - vLoss * 0.15 - Math.max(0, jitter - 20) * 0.025));
        const quality = mos >= 4 ? 'Good' : mos >= 3.6 ? 'Fair' : 'Poor';
        if (a.congested && !s.qos && quality === 'Poor') s.seen.badNoQos = true;
        if (a.congested && s.qos && quality === 'Good') s.seen.goodQos = true;
        el.append(h('div', { class: 'w-title' }, `CONGESTION LAB · ${cap} kbps WAN`, api.state.done ? h('span', { class: 'ok' }, '✓ COMPLETE') : null));
        el.append(h('p', { class: 'small muted' }, `Two voice calls need ${voice} kbps. Add backup traffic and watch what the T1 does when the offered load exceeds ${cap} kbps.`));
        const slider = h('input', { type: 'range', min: 0, max: 2400, step: 100, value: s.bulk, 'data-testid': `cs-bulk-${id}`, 'aria-label': 'Backup traffic (kbps)', oninput: (e) => { s.bulk = +e.target.value; render(); }, onchange: () => { rt.action('congestion', { bulk: s.bulk, qos: s.qos }); api.save(); } });
        el.append(h('label', { class: 'fld' }, h('span', {}, `Backup traffic: ${s.bulk} kbps · total offered ${voice + +s.bulk} kbps`), slider));
        el.append(h('label', { class: 'radio small' }, h('input', { type: 'checkbox', checked: s.qos || null, 'data-testid': `cs-qos-${id}`, onchange: (e) => { s.qos = e.target.checked; rt.action('congestion', { bulk: s.bulk, qos: s.qos }); api.save(); render(); } }), 'QoS: low-latency queue (priority 256 kbps) for voice'));
        const bar = (label, off, srv) => h('div', { class: 'cs-row' }, h('b', {}, label),
          h('div', { class: 'cs-bar', role: 'img', 'aria-label': `${label}: ${Math.round(srv)} of ${off} kbps delivered` }, h('span', { class: 'srv', style: { width: `${Math.min(100, (srv / cap) * 100)}%` } }), h('span', { class: 'drop', style: { width: `${Math.min(100, ((off - srv) / cap) * 100)}%` } })),
          h('span', { class: 'mono small' }, `${Math.round(srv)}/${off} kbps · ${off ? Math.round(100 * (1 - srv / off)) : 0}% dropped`));
        el.append(h('div', { class: 'cs' }, bar('Voice', voice, served.voice), bar('Backup', +s.bulk, served.bulk)));
        el.append(h('div', { class: `w-fb ${quality === 'Good' ? 'good' : 'bad'}`, 'data-testid': `cs-quality-${id}` }, `Link ${a.congested ? 'CONGESTED' : 'not congested'} · voice jitter ≈ ${jitter} ms · call quality ${quality} (MOS ${mos.toFixed(1)})`));
        if (!api.state.done && s.seen.badNoQos && s.seen.goodQos) { rt.action('congestion-complete'); api.done(); }
        el.append(h('p', { class: 'small muted' }, api.state.done ? 'You saw it both ways: same load, same link — QoS decided who waits and who is dropped.' : `To complete: ${s.seen.badNoQos ? '✓' : '○'} make the calls fail with QoS off · ${s.seen.goodQos ? '✓' : '○'} rescue them with QoS on at a congested load`));
      };
      render();
    },
  };
}

// ------------------------------------------------------------------ build a sequence
/** Click the pieces in the right order (e.g. outermost header first). */
export function sequence({ id, title, intro, pieces, order }) {
  return {
    id, blocker: `Complete: ${title}`,
    verify: (d) => Array.isArray(d?.seq) && d.seq.length === order.length && d.seq.every((k, i) => k === order[i]),
    mount(el, rt, api) {
      const s = api.state.data;
      s.seq = s.seq || [];
      const render = (msg) => {
        el.textContent = '';
        el.append(h('div', { class: 'w-title' }, title, api.state.done ? h('span', { class: 'ok' }, '✓ COMPLETE') : null));
        if (intro) el.append(h('p', { class: 'small muted' }, intro));
        el.append(h('div', { class: 'seq-built', 'data-testid': `seq-built-${id}` }, s.seq.length ? s.seq.map((k) => h('span', { class: 'seq-piece on' }, pieces[k])) : h('span', { class: 'muted small' }, '(empty — click the pieces below)')));
        if (!api.state.done) {
          el.append(h('div', { class: 'opt-row' }, Object.keys(pieces).filter((k) => !s.seq.includes(k)).map((k) => h('button', { class: 'opt', 'data-testid': `seq-${id}-${k}`, onclick: () => { s.seq.push(k); api.save(); render(); } }, pieces[k]))));
          el.append(h('div', { class: 'row' },
            h('button', { class: 'btn small ghost', 'data-testid': `seq-reset-${id}`, onclick: () => { s.seq = []; api.save(); render(); } }, 'Start again'),
            h('button', { class: 'btn small', 'data-testid': `seq-check-${id}`, onclick: () => {
              const ok = s.seq.length === order.length && s.seq.every((k, i) => k === order[i]);
              rt.action('sequence', { widget: id, ok });
              if (ok) { api.done(); render(); } else render('Not quite. Think about what the carrier needs to read first, and what must stay hidden inside.');
            } }, 'Check order')));
        }
        if (msg) el.append(h('div', { class: 'w-fb bad' }, msg));
      };
      render();
    },
  };
}

// ------------------------------------------------------------------ user / NMS report board
/** "Refresh reports" board: symptoms only, computed from live state by `lines(rt)`. */
export function reportBoard({ id, title = 'USER & NMS REPORTS', lines, required = false }) {
  return {
    id, required,
    verify: () => true, // "Refresh reports": the reports themselves are recomputed from the replayed network
    mount(el, rt, api) {
      const s = api.state.data;
      const render = () => {
        el.textContent = '';
        el.append(h('div', { class: 'w-title' }, title, h('span', {}, s.t ? `as of ${s.t}` : '')),
          h('div', { class: 'row' }, h('button', { class: 'btn small', 'data-testid': 'status-refresh', onclick: () => {
            s.lines = lines(rt); s.t = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            rt.action('status-refresh'); api.done(); api.save(); render();
          } }, 'Refresh reports')));
        if (s.lines) el.append(h('ul', { class: 'small', 'data-testid': `report-${id}` }, s.lines.map((l) => h('li', {}, l))));
      };
      render();
    },
  };
}
